import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Loader2, Ticket, X } from "lucide-react";
import { applyVoucher, clearApplied, fetchStoreVouchers } from "../../store/voucherSlices/storeVoucherSlice";
import { formatINR } from "./VoucherCheckoutPanel";

// redemption_type: 1 online, 2 offline, 3 both. Offline-only vouchers can't be used on an app payment.
const OFFLINE_ONLY = 2;

const isUsable = (v) => {
    const now = Date.now();
    if (v?.status !== "active" || v?.used_at) return false;
    if (v?.valid_from && new Date(v.valid_from).getTime() > now) return false;
    if (v?.valid_to && new Date(v.valid_to).getTime() < now) return false;
    return Number(v?.voucher?.redemption_type) !== OFFLINE_ONLY;
};

const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

export default function StoreVoucherPicker({ storeId, billAmount = 0 }) {
    const dispatch = useDispatch();
    const { list, listStatus, applied, applying, applyError } = useSelector((s) => s.storeVoucher);
    const [code, setCode] = useState("");
    const [pendingCode, setPendingCode] = useState(null);

    useEffect(() => {
        dispatch(fetchStoreVouchers());
    }, [dispatch]);

    // Vouchers of this store only, usable ones
    const storeVouchers = useMemo(
        () => list.filter((v) => Number(v?.voucher?.store_id) === Number(storeId) && isUsable(v)),
        [list, storeId]
    );

    const apply = (voucherCode, name) => {
        setPendingCode(voucherCode);
        dispatch(applyVoucher({ code: voucherCode,online:true, storeId, billAmount, name }));
    };

    const handleInputApply = () => apply(code.trim(), undefined);

    // Applied state
    if (applied) {
        return (
            <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
                <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-emerald-800">Voucher applied</p>
                        <p className="truncate text-xs text-emerald-700">
                            {applied.name ? `${applied.name} · ` : ""}
                            {applied.code}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => dispatch(clearApplied())}
                        className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm hover:bg-emerald-100"
                    >
                        <X size={14} /> Remove
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <header className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 sm:px-6">
                <Ticket size={18} className="text-[#0B67FC]" />
                <h2 className="text-base font-semibold text-slate-900">Vouchers</h2>
            </header>

            <div className="space-y-4 px-5 py-4 sm:px-6">
                {/* Available vouchers for this store */}
                {listStatus === "loading" && <div className="h-24 animate-pulse rounded-xl bg-slate-100" />}

                {storeVouchers.length > 0 && (
                    <div>
                        <p className="mb-2 text-xs font-medium text-slate-500">Available at this store</p>
                        <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-2">
                            {storeVouchers.map((v) => {
                                const min = Number(v.voucher?.min_purchase_value) || 0;
                                const value = Number(v.voucher?.voucher_price) || 0;
                                const minOk = billAmount >= min;
                                const valueOk = billAmount >= value;
                                const eligible = minOk && valueOk;
                                const till = formatDate(v.valid_to);
                                const busy = applying && pendingCode === v.voucher_code;
                                return (
                                    <div
                                        key={v.id}
                                        className={`w-60 shrink-0 snap-start rounded-xl border border-dashed p-3 ${
                                            eligible ? "border-[#0B67FC]/40 bg-[#0B67FC]/5" : "border-slate-200 bg-slate-50 opacity-70"
                                        }`}
                                    >
                                        <p className="truncate text-sm font-semibold text-slate-900">{v.voucher_name}</p>
                                        <p className="mt-1 text-xs tracking-wider text-slate-500">{v.voucher_code}</p>
                                        {value > 0 && <p className="mt-1 text-xs font-medium text-[#0B67FC]">Voucher value {formatINR(value)}</p>}
                                        <p className="mt-1 text-xs text-slate-500">
                                            {min > 0 ? `Min bill ${formatINR(min)}` : "No minimum bill"}
                                            {till ? ` · Valid till ${till}` : ""}
                                        </p>
                                       <button
                                            type="button"
                                            disabled={!eligible || applying}
                                            onClick={() => apply(v.voucher_code, v.voucher_name)}
                                            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#0B67FC] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#0954D0] disabled:cursor-not-allowed disabled:bg-slate-300"
                                        >
                                            {busy && <Loader2 size={14} className="animate-spin" />}
                                            Apply
                                        </button>
                                        {!minOk && (
                                            <p className="mt-1.5 text-[11px] font-medium text-amber-700">
                                                Add {formatINR(min - billAmount)} more to use this voucher
                                            </p>
                                        )}
                                        {minOk && !valueOk && (
                                            <p className="mt-1.5 text-[11px] font-medium text-amber-700">
                                                Can't use: bill value must be at least {formatINR(value)}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Have a code */}
                <div>
                    <p className="mb-2 text-xs font-medium text-slate-500">Have a voucher code?</p>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === "Enter" && code.trim() && !applying && handleInputApply()}
                            placeholder="Enter voucher code"
                            className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm tracking-wider outline-none focus:border-[#0B67FC]"
                        />
                        <button
                            type="button"
                            onClick={handleInputApply}
                            disabled={!code.trim() || applying}
                            className="flex items-center gap-1.5 rounded-lg bg-[#0B67FC] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0954D0] disabled:bg-slate-300"
                        >
                            {applying && pendingCode === code.trim() && <Loader2 size={14} className="animate-spin" />}
                            Apply
                        </button>
                    </div>
                    {applyError && <p className="mt-2 text-xs text-rose-600">{applyError}</p>}
                </div>
            </div>
        </section>
    );
}